import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import UploadPolicyModal from "../renewals/UploadPolicy";
import RenewPolicy from "../renewals/RenewPolicy";
import SuccessDialog from "../addPolicy/SuccessDialog";
import { getVehicleTypeDisplayName } from "../../utils/vehicleTypeMapper";
import { fetchPolicyById } from "../policies/policySlice";
import { firebaseCache } from "../../utils/firebaseCache";
import { incrementDailyPolicyCounter } from "../../utils/dailyPolicyCounter";

// MUI
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import DescriptionIcon from "@mui/icons-material/Description";
import BusinessIcon from "@mui/icons-material/Business";
import TagIcon from "@mui/icons-material/LocalOffer";
import ListAltIcon from "@mui/icons-material/ListAlt";
import FlagIcon from "@mui/icons-material/Flag";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import HandymanIcon from "@mui/icons-material/Handyman";
import NumbersIcon from "@mui/icons-material/Numbers";
import SouthWestIcon from "@mui/icons-material/SouthWest";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PaymentIcon from "@mui/icons-material/Payment";
import CommentIcon from "@mui/icons-material/Comment";
import ShareIcon from "@mui/icons-material/Share";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import useMediaQuery from "@mui/material/useMediaQuery";
import { shouldUseMobileLayout } from "../../utils/deviceDetection";
import StatusBadge from "../../components/common/StatusBadge";
import { calculatePolicyStatus } from "../../utils/dateUtils";
import { createWhatsAppMessage } from "../../utils/urlShortener";
import CircularProgress from '@mui/material/CircularProgress';

const PolicyDetail = () => {
  const dispatch = useDispatch();
  const { id: policyId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { currentPolicy } = useSelector((state) => state.policies);

  // Get the page and category we came from, default to 1 and 'motor' if not provided
  const fromPage = location.state?.fromPage || 1;
  const fromCategory = location.state?.fromCategory || 'motor';
  const veryShortScreen = useMediaQuery('(max-height:700px)');
  const shortScreen = useMediaQuery('(max-height:850px)');
  const mediumScreen = useMediaQuery('(max-width:1000px)');
  const isMobile = shouldUseMobileLayout();
  const isSmallScreen = useMediaQuery('(max-width:1281px)');

  const [policy, setPolicy] = useState(location.state?.policy || null);
  const [client, setClient] = useState(location.state?.client || null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showRenewForm, setShowRenewForm] = useState(false);
  const [prefillData, setPrefillData] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDriveFileMissing, setIsDriveFileMissing] = useState(false);
  const [renewMode, setRenewMode] = useState("edit");
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successPdfUrl, setSuccessPdfUrl] = useState(null);
  const [pdfAction, setPdfAction] = useState(null); // 'viewing' or 'sharing'

  const fetchPolicy = async (preservePdfUrl = false) => {
    try {
      setLoading(true);
      const userId = location.state?.policy?.agentId || user?.uid || location.state?.userId;
      if (!userId || !policyId) {
        console.error("❌ Missing userId or policyId");
        return;
      }

      // Use Redux action for optimized fetching with caching
      const result = await dispatch(fetchPolicyById({ agentId: userId, id: policyId }));

      // Get the fetched policy from the dispatch result
      const fetchedPolicy = result.payload;

      if (fetchedPolicy) {
        if (preservePdfUrl && policy?.pdfUrl) {
          // Preserve existing PDF URL during edits
          setPolicy({ ...fetchedPolicy, pdfUrl: policy.pdfUrl });
        } else {
          setPolicy(fetchedPolicy);
        }

        // Parallel check for Drive file existence if applicable
        if (fetchedPolicy.pdfSource === "drive" && fetchedPolicy.driveFileId) {
          try {
            const { checkFilesExistence } = await import("../../utils/driveApi");
            const existenceMap = await checkFilesExistence([fetchedPolicy.driveFileId]);
            if (existenceMap[fetchedPolicy.driveFileId] === false) {
              console.warn("⚠️ Drive file is missing for this policy");
              setIsDriveFileMissing(true);
            } else {
              setIsDriveFileMissing(false);
            }
          } catch (driveErr) {
            console.error("❌ Error checking Drive file existence:", driveErr);
            // Don't mark as missing on API error, let the actual action handle it
          }
        } else {
          setIsDriveFileMissing(false);
        }
      } else {
        console.warn("❌ Policy not found.");
      }
    } catch (err) {
      console.error("🔥 Error fetching policy:", err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    // Log debugging info (temporary, can be removed)
    // console.log("PolicyDetail mounted. ID:", policyId, "User:", user?.uid);
    // console.log("Location State:", location.state);

    const effectiveUserId = location.state?.policy?.agentId || user?.uid || location.state?.userId;

    if (policyId && effectiveUserId) {
      fetchPolicy();
    } else {
      console.log("Waiting for user/policyId...", { policyId, user: user?.uid });
    }
  }, [policyId, user?.uid, location.state]);

  // Handle successful policy update
  const handlePolicyUpdateSuccess = async (pdfUrl, updatedPolicyData) => {
    console.log("🎉 Policy update success called with mode:", renewMode, "pdfUrl:", pdfUrl, "updatedData:", updatedPolicyData);
    setIsRefreshing(true);
    setShowRenewForm(false);
    setPrefillData(null);

    // Increment policy counter if this is a new policy (renew/update mode), not an edit
    if (renewMode !== "edit") {
      try {
        incrementDailyPolicyCounter(user?.uid);
        console.log('📊 Daily policy counter incremented after successful renewal');
      } catch (error) {
        console.error('❌ Error incrementing daily policy counter:', error);
      }
    }

    // Mark policy cache for invalidation (PolicyDetails write operation)
    firebaseCache.invalidateAfterWrite('policydetails_write', user?.uid);
    console.log("✅ Policy cache marked for invalidation after update");

    // Update the policy state immediately with the new data for instant UI update
    if (updatedPolicyData) {
      setPolicy(updatedPolicyData);
      console.log("✅ Policy state updated immediately with new data");
    }

    // Show success dialog only for renewals, banner for edits
    if (renewMode === "update" && pdfUrl) {
      // Renewal - show SuccessDialog
      setSuccessPdfUrl(pdfUrl);
      setShowSuccessDialog(true);
    } else {
      // Edit - show toast banner
      toast.success("Policy successfully edited", {
        containerId: "bottom-center",
        position: "bottom-center",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        closeButton: false,
        style: {
          backgroundColor: '#DCFCE7',
          color: '#FFFFFF',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: 500,
          padding: '12px 16px',
          minHeight: '44px',
          alignItems: 'center',
          gap: '2px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          marginBottom: '75vh',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: 'auto',
          maxWidth: 'none',
          display: 'inline-flex',
        },
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ color: '#FFFFFF' }}
          >
            <path
              d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      });
    }

    // Refresh the policy data in background to ensure sync with database
    if (renewMode === "edit") {
      // For edits, preserve the existing PDF URL
      await fetchPolicy(true);
    } else {
      // For renewals, fetch normally to get updated data
      await fetchPolicy();
    }
  };


  // Handle share PDF
  const handleSharePdf = async () => {
    if (!policy?.pdfUrl && !policy?.driveFileId) {
      console.error("No PDF URL or Drive file available for sharing");
      return;
    }

    if (pdfAction) return;
    setPdfAction('sharing');

    try {
      let shareUrl = policy.pdfUrl;

      // For Drive-stored policies, create a shareable link
      if (policy.pdfSource === "drive" && policy.driveFileId) {
        try {
          const { createDriveShareLink } = await import("../../utils/driveApi");
          const shareLinks = await createDriveShareLink(policy.driveFileId);
          shareUrl = shareLinks.shareableLink;
          console.log("✅ Drive share link created:", shareUrl);
        } catch (driveError) {
          console.error("❌ Failed to create Drive share link:", driveError);
          if (policy.pdfUrl) {
            console.log("🔗 Using fallback pdfUrl for sharing");
            shareUrl = policy.pdfUrl;
          } else {
            const isNetworkError = driveError.message?.includes('Failed to fetch') || driveError.message?.includes('NetworkError');
            toast.error(isNetworkError ? "Connection error: Could not reach backend server." : "Unable to create share link. Document may have been deleted.");
            return;
          }
        }
      }

      const shareText = await createWhatsAppMessage(shareUrl, user?.displayName, policy.id, true);
      const whatsappAppUrl = `whatsapp://send?text=${encodeURIComponent(shareText)}`;
      const webWhatsappUrl = `https://web.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;

      // Try app first using a hidden iframe to avoid console errors
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = whatsappAppUrl;
      document.body.appendChild(iframe);

      // Remove iframe after a short delay
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 100);

      // Listen for page blur (indicates app opened)
      let appOpened = false;
      const handleBlur = () => {
        appOpened = true;
        window.removeEventListener('blur', handleBlur);
      };
      window.addEventListener('blur', handleBlur);

      // Fallback to web after 1.5 seconds ONLY if app didn't open
      setTimeout(() => {
        window.removeEventListener('blur', handleBlur);
        if (!appOpened) {
          window.open(webWhatsappUrl, "_blank", "noopener,noreferrer");
        }
      }, 1500);
    } catch (error) {
      console.error("Error sharing PDF:", error);
      const isNetworkError = error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError');
      toast.error(isNetworkError ? "Connection error: Could not reach backend server." : "Failed to share policy");
    } finally {
      setPdfAction(null);
    }
  };

  // Handle view PDF
  const handleViewPdf = async () => {
    if (!policy?.pdfUrl && !policy?.driveFileId) {
      console.error("No PDF URL or Drive file available for viewing");
      return;
    }

    if (pdfAction) return;
    setPdfAction('viewing');

    try {
      // For Drive-stored policies, get view URL from backend
      if (policy.pdfSource === "drive" && policy.driveFileId) {
        try {
          const { getDriveViewUrl } = await import("../../utils/driveApi");
          const viewUrl = await getDriveViewUrl(policy.driveFileId);
          window.open(viewUrl, "_blank", "noopener,noreferrer");
          return;
        } catch (driveError) {
          console.error("❌ Failed to get Drive view URL:", driveError);
          if (policy.pdfUrl) {
            console.log("🔗 Using fallback pdfUrl for viewing");
            window.open(policy.pdfUrl, "_blank", "noopener,noreferrer");
            return;
          }
          const isNetworkError = driveError.message?.includes('Failed to fetch') || driveError.message?.includes('NetworkError');
          toast.error(isNetworkError ? "Connection error: Could not reach backend server." : "Document unavailable. It may have been deleted from Google Drive.");
          return;
        }
      }

      // Firebase-stored policies - use direct URL
      window.open(policy.pdfUrl, "_blank", "noopener,noreferrer");
    } finally {
      setPdfAction(null);
    }
  };

  if (!policy && loading) {
    return (
      <Box sx={{ px: 2, pt: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography variant="h6" sx={{ color: '#757575' }}>Loading policy details...</Typography>
      </Box>
    );
  }

  if (!policy && !loading) {
    return (
      <Box sx={{ px: 2, pt: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography variant="h6" sx={{ color: '#757575' }}>Policy not found.</Typography>
      </Box>
    );
  }

  // Compute simple status from expiry (fallback to provided status)
  const computeStatus = () => {
    return calculatePolicyStatus(policy.expiryDate, policy.status) || "-";
  };

  const statusText = computeStatus();

  return (
    <Box sx={{ px: 2, pt: 1, pb: 10, minHeight: 'fit-content', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton
          onClick={() => {
            // Check if we came from a specific screen and navigate back accordingly
            const state = location.state;
            if (state?.fromScreen === 'renewals') {
              navigate('/renewals', { state: { currentPage: state.fromPage, selectedDate: state.selectedDate } });
            } else if (state?.fromScreen === 'balance') {
              navigate('/balance', { state: { currentPage: state.fromPage, fromCategory: state.fromCategory } });
            } else if (state?.fromScreen === 'policies') {
              navigate('/policy', { state: { fromCategory: state.fromCategory } });
            } else if (state?.fromScreen === 'client') {
              navigate('/clientDetail', { state: { currentPage: state.fromPage } });
            } else if (state?.fromScreen === 'clientDetail') {
              console.log('PolicyDetail: Navigating back to client detail with clientFromScreen:', state?.clientFromScreen, 'clientFromPage:', state?.clientFromPage);
              navigate(`/client/${state.clientId}`, {
                state: {
                  fromScreen: state?.clientFromScreen,
                  fromPage: state?.clientFromPage,
                  client: client
                }
              });
            } else {
              // Default to policy list with page and category state
              console.log('PolicyDetail: Navigating back to policy list with fromPage:', fromPage, 'fromCategory:', fromCategory);
              navigate('/policy', { state: { currentPage: fromPage, fromCategory: fromCategory } });
            }
          }}
          size="small"
          sx={{
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.04)",
            },
          }}
        >
          <ArrowBackIcon sx={{ color: "#212121" }} />
        </IconButton>
        <Typography variant="h5" sx={{
          fontFamily: 'Roboto Slab',
          fontWeight: 550,
          fontSize: '32px',
          lineHeight: '56px'
        }}>Policy Details</Typography>
      </Box>

      {/* Client card - left aligned */}
      <Paper
        sx={{
          width: '100%',
          height: 100,
          gap: '16px',
          px: { xs: 2, sm: 4 },
          py: { xs: 2, sm: 3 },
          boxShadow: 'none',
          backgroundColor: '#0063F7',
          color: '#FFFFFF',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-start',
          mb: 0,
        }}
      >
        <Avatar sx={{ width: 48, height: 48, bgcolor: '#E0EBFF', color: '#0063F7', mr: 2 }}>
          <PersonIcon sx={{ fontSize: 28 }} />
        </Avatar>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', flex: 1 }}>
          <Typography sx={{
            fontFamily: 'Roboto',
            fontWeight: 500,
            fontSize: '18px',
            lineHeight: '28px',
            letterSpacing: '0.01em',
            opacity: 1,
            textAlign: 'left',
            mb: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '24px'
          }}>
            {policy.name || '-'}
            {policy.referredBy && (
              <Typography component="span" sx={{
                fontFamily: 'Roboto',
                fontWeight: 500,
                fontSize: '18px',
                lineHeight: '28px',
                letterSpacing: '0.01em',
                opacity: 0.9,
                ml: 2
              }}>
                Referred By: {policy.referredBy}
              </Typography>
            )}
          </Typography>
          <Typography sx={{
            fontFamily: 'Roboto',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.01em',
            color: '#FFFFFF',
            opacity: 1,
            textAlign: 'left'
          }}>{policy.mobile || '-'}</Typography>
        </Box>

        {/* Share PDF and View PDF buttons */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            onClick={handleSharePdf}
            disabled={(!policy?.pdfUrl && !policy?.driveFileId) || isDriveFileMissing || pdfAction === 'sharing'}
            sx={{
              width: 120,
              height: 40,
              borderRadius: '8px',
              backgroundColor: '#166434 !important',
              color: '#FFFFFF !important',
              fontFamily: 'Roboto',
              fontWeight: 500,
              fontSize: '14px',
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
              gap: 1,
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                backgroundColor: '#166434 !important',
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.3) !important',
                color: 'rgba(255, 255, 255, 0.5) !important',
              }
            }}
          >
            {pdfAction === 'sharing' ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <ShareIcon sx={{ fontSize: 18 }} />}
            Share Pdf
          </Button>

          <Button
            onClick={handleViewPdf}
            disabled={(!policy?.pdfUrl && !policy?.driveFileId) || isDriveFileMissing || pdfAction === 'viewing'}
            sx={{
              minWidth: 120,
              height: 40,
              borderRadius: '8px',
              backgroundColor: '#FFFFFF !important',
              color: '#0063F7 !important',
              fontFamily: 'Roboto',
              fontWeight: 500,
              fontSize: '14px',
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
                backgroundColor: '#FFFFFF !important',
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.3) !important',
                color: 'rgba(255, 255, 255, 0.5) !important',
              }
            }}
          >
            {pdfAction === 'viewing' ? <CircularProgress size={18} sx={{ color: '#0063F7' }} /> : <PictureAsPdfIcon sx={{ fontSize: 18 }} />}
            View Pdf
          </Button>
        </Box>
      </Paper>

      {/* Main content container */}
      <Box
        sx={{
          width: '100%',
          background: '#fff',
          borderRadius: { xs: '0 0 12px 12px', sm: '0 0 16px 16px' },
          boxShadow: 'none',
          px: { xs: 2, sm: 3 },
          pt: 2,
          pb: 3,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          gap: 0,
          flexShrink: 0
        }}
      >
        {/* Details sections */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, px: 0, py: 2 }}>
          {policy?.policyCategory === 'motor' && (
            <Box>
              <Box sx={{ pt: 1, pb: 0.5, pl: 2 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: '18px',
                    fontWeight: 600,
                  }}
                >
                  Vehicle Details
                </Typography>
              </Box>
              <Box
                sx={{
                  background: "#fff",
                  borderTop: '1px solid #E5E7EB',
                  padding: "12px 20px",
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
                  rowGap: "16px",
                  columnGap: "16px",
                  alignItems: "start",
                  boxShadow: 'none',
                }}
              >
                <Box sx={{
                  display: "grid",
                  gridTemplateRows: "auto auto",
                  alignItems: "start",
                  flexGrow: 1,
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <DirectionsCarIcon sx={{ fontSize: 16, color: "#626B77" }} />
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#626B77",
                      }}
                    >
                      Vehicle Type
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "flex-start", ml: 2.25 }}>
                    <Typography
                      sx={{
                        fontSize: "18px !important",
                        fontWeight: 500,
                        color: "#000",
                      }}
                    >
                      {getVehicleTypeDisplayName(policy.vehicleType)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{
                  display: "grid",
                  gridTemplateRows: "auto auto",
                  alignItems: "start",
                  flexGrow: 1,
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <HandymanIcon sx={{ fontSize: 16, color: "#626B77" }} />
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#626B77",
                      }}
                    >
                      Make & Model
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "flex-start", ml: 2.25 }}>
                    <Typography
                      sx={{
                        fontSize: "18px !important",
                        fontWeight: 500,
                        color: "#000",
                      }}
                    >
                      {policy.vehicleMake ? `${policy.vehicleMake} ${policy.vehicleModel || ''}`.trim() : (policy.vehicleModel || '-')}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{
                  display: "grid",
                  gridTemplateRows: "auto auto",
                  alignItems: "start",
                  flexGrow: 1,
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <NumbersIcon sx={{ fontSize: 16, color: "#626B77" }} />
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#626B77",
                      }}
                    >
                      Vehicle Number
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "flex-start", ml: 2.25 }}>
                    <Typography
                      sx={{
                        fontSize: "18px !important",
                        fontWeight: 500,
                        color: "#000",
                      }}
                    >
                      {policy.vehicleNumber || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          <Box>
            <Box sx={{ pt: 1, pb: 0.5, pl: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '18px',
                  fontWeight: 600,
                }}
              >
                Policy Details
              </Typography>
            </Box>
            <Box
              sx={{
                background: "#fff",
                borderTop: '1px solid #E5E7EB',
                padding: "12px 20px",
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
                rowGap: "16px",
                columnGap: "16px",
                alignItems: "start",
                boxShadow: 'none',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <BusinessIcon sx={{ fontSize: 16, color: "#626B77" }} />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#626B77",
                    }}
                  >
                    Company
                  </Typography>
                </Box>
                <Box sx={{ mt: 1, ml: 2.25 }}>
                  <Typography
                    sx={{
                      fontSize: "18px !important",
                      fontWeight: 500,
                      color: "#000",
                    }}
                  >
                    {policy.policyCompany || '-'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ListAltIcon sx={{ fontSize: 16, color: "#626B77" }} />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#626B77",
                    }}
                  >
                    Policy No
                  </Typography>
                </Box>
                <Box sx={{ mt: 1, ml: 2.25 }}>
                  <Typography
                    sx={{
                      fontSize: "18px !important",
                      fontWeight: 500,
                      color: "#000",
                    }}
                  >
                    {policy.policyNumber || '-'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <DescriptionIcon sx={{ fontSize: 16, color: "#626B77" }} />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#626B77",
                    }}
                  >
                    Policy Type
                  </Typography>
                </Box>
                <Box sx={{ mt: 1, ml: 2.25 }}>
                  <Typography
                    sx={{
                      fontSize: "18px !important",
                      fontWeight: 500,
                      color: "#000",
                    }}
                  >
                    {policy.policyType || '-'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarTodayIcon sx={{ fontSize: 16, color: "#626B77" }} />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#626B77",
                    }}
                  >
                    Expiry Date
                  </Typography>
                </Box>
                <Box sx={{ mt: 1, ml: 2.25 }}>
                  <Typography
                    sx={{
                      fontSize: "18px !important",
                      fontWeight: 500,
                      color: "#E11D48",
                    }}
                  >
                    {policy.expiryDate || '-'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FlagIcon sx={{ fontSize: 16, color: "#626B77" }} />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#626B77",
                    }}
                  >
                    Status
                  </Typography>
                </Box>
                <Box sx={{ mt: 1, ml: 2.25 }}>
                  <StatusBadge status={statusText} size={isSmallScreen ? 'small' : 'normal'} />
                </Box>
              </Box>
              {policy?.policyCategory !== 'motor' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TagIcon sx={{ fontSize: 16, color: "#626B77" }} />
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#626B77",
                      }}
                    >
                      Sum Insured
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 1, ml: 2.25 }}>
                    <Typography
                      sx={{
                        fontSize: "18px !important",
                        fontWeight: 500,
                        color: "#000",
                      }}
                    >
                      {policy.sumInsured ? parseFloat(policy.sumInsured).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <Box>
            <Box sx={{ pt: 1, pb: 0.5, pl: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '18px',
                  fontWeight: 600,
                }}
              >
                Payment Details
              </Typography>
            </Box>
            <Box
              sx={{
                background: "#fff",
                borderTop: '1px solid #E5E7EB',
                padding: "12px 20px",
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
                rowGap: "16px",
                columnGap: "16px",
                alignItems: "start",
                boxShadow: 'none',
              }}
            >
              {policy?.policyCategory === "motor" && (
                <>
                  {policy.policyType !== "Liability" && (
                    <Box sx={{
                      display: "grid",
                      gridTemplateRows: "auto auto",
                      alignItems: "start",
                      flexGrow: 1,
                    }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <CurrencyRupeeIcon sx={{ fontSize: 16, color: "#626B77" }} />
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 400,
                            color: "#626B77",
                          }}
                        >
                          OD Premium
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "flex-start", ml: 2.25 }}>
                        <Typography
                          sx={{
                            fontSize: "18px !important",
                            fontWeight: 500,
                            color: "#000",
                          }}
                        >
                          {policy.odPremium ? parseFloat(policy.odPremium).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {(policy.policyType === "Package" || policy.policyType === "Liability") && (
                    <Box sx={{
                      display: "grid",
                      gridTemplateRows: "auto auto",
                      alignItems: "start",
                      flexGrow: 1,
                    }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <CurrencyRupeeIcon sx={{ fontSize: 16, color: "#626B77" }} />
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 400,
                            color: "#626B77",
                          }}
                        >
                          TP Premium
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "flex-start", ml: 2.25 }}>
                        <Typography
                          sx={{
                            fontSize: "18px !important",
                            fontWeight: 500,
                            color: "#000",
                          }}
                        >
                          {policy.tpPremium ? parseFloat(policy.tpPremium).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </>
              )}
              <Box sx={{
                display: "grid",
                gridTemplateRows: "auto auto",
                alignItems: "start",
                flexGrow: 1,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CurrencyRupeeIcon sx={{ fontSize: 16, color: "#626B77" }} />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#626B77",
                    }}
                  >
                    Net Premium
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", ml: 2.25 }}>
                  <Typography
                    sx={{
                      fontSize: "18px !important",
                      fontWeight: 500,
                      color: "#000",
                    }}
                  >
                    {policy.netPremium ? parseFloat(policy.netPremium).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{
                display: "grid",
                gridTemplateRows: "auto auto",
                alignItems: "start",
                flexGrow: 1,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CurrencyRupeeIcon sx={{ fontSize: 16, color: "#626B77" }} />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#626B77",
                    }}
                  >
                    Total Premium
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", ml: 2.25 }}>
                  <Typography
                    sx={{
                      fontSize: "18px !important",
                      fontWeight: 500,
                      color: "#000",
                    }}
                  >
                    {policy.premiumAmount ? parseFloat(policy.premiumAmount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{
                display: "grid",
                gridTemplateRows: "auto auto",
                alignItems: "start",
                flexGrow: 1,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CurrencyRupeeIcon sx={{ fontSize: 16, color: "#626B77" }} />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#626B77",
                    }}
                  >
                    Customer Discount
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", ml: 2.25 }}>
                  <Typography
                    sx={{
                      fontSize: "18px !important",
                      fontWeight: 500,
                      color: "#000",
                    }}
                  >
                    {policy.discount ? parseFloat(policy.discount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{
                display: "grid",
                gridTemplateRows: "auto auto",
                alignItems: "start",
                flexGrow: 1,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CurrencyRupeeIcon sx={{ fontSize: 16, color: "#626B77" }} />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#626B77",
                    }}
                  >
                    Amount Payable
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", ml: 2.25 }}>
                  <Typography
                    sx={{
                      fontSize: "18px !important",
                      fontWeight: 500,
                      color: "#000",
                    }}
                  >
                    {policy.amountPayable ? parseFloat(policy.amountPayable).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) :
                      (policy.premiumAmount ? (parseFloat(policy.premiumAmount) - (parseFloat(policy.discount) || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{
                display: "grid",
                gridTemplateRows: "auto auto",
                alignItems: "start",
                flexGrow: 1,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <SouthWestIcon sx={{ fontSize: 16, color: "#626B77" }} />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#626B77",
                    }}
                  >
                    Received
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", ml: 2.25 }}>
                  <Typography
                    sx={{
                      fontSize: "18px !important",
                      fontWeight: 500,
                      color: "#000",
                    }}
                  >
                    {policy.paidAmount ? parseFloat(policy.paidAmount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{
                display: "grid",
                gridTemplateRows: "auto auto",
                alignItems: "start",
                flexGrow: 1,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <AccountBalanceWalletIcon sx={{ fontSize: 16, color: "#626B77" }} />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#626B77",
                    }}
                  >
                    Balance
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", ml: 2.25 }}>
                  <Typography
                    sx={{
                      fontSize: "18px !important",
                      fontWeight: 500,
                      color: "#E11D48",
                    }}
                  >
                    {Math.max((Number(policy.amountPayable || (parseFloat(policy.premiumAmount) - (parseFloat(policy.discount) || 0))) || 0) - (Number(policy.paidAmount) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{
                display: "grid",
                gridTemplateRows: "auto auto",
                alignItems: "start",
                flexGrow: 1,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PaymentIcon sx={{ fontSize: 16, color: "#626B77" }} />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#626B77",
                    }}
                  >
                    Mode of Payment
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", ml: 2.25 }}>
                  <Typography
                    sx={{
                      fontSize: "18px !important",
                      fontWeight: 500,
                      color: "#000",
                    }}
                  >
                    {policy.modeOfPayment || '-'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{
                display: "grid",
                gridTemplateRows: "auto auto",
                alignItems: "start",
                flexGrow: 1,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CommentIcon sx={{ fontSize: 16, color: "#626B77" }} />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#626B77",
                    }}
                  >
                    Remarks
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", ml: 2.25 }}>
                  <Typography
                    sx={{
                      fontSize: "18px !important",
                      fontWeight: 500,
                      color: "#000",
                    }}
                  >
                    {policy.remarks || '-'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Buttons outside the white container - Hidden on mobile */}
      {!isMobile && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'center', md: 'flex-end' },
            gap: 2,
            width: '100%',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            mt: 2,
          }}
        >
          <Button
            onClick={() => {
              setRenewMode("edit");
              setPrefillData(null);
              setShowRenewForm(true);
            }}
            disabled={isRefreshing}
            sx={{
              width: { xs: '100%', sm: 160 },
              height: 60,
              borderRadius: '8px',
              p: '16px 12px 16px 8px',
              gap: '4px',
              boxShadow: 0,
              backgroundColor: isRefreshing ? '#ccc' : '#E0EBFF',
              color: isRefreshing ? '#999' : '#0063F7',
              fontFamily: 'Roboto',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '24px',
              letterSpacing: '0.01em',
              '&:hover': {
                backgroundColor: isRefreshing ? '#ccc' : '#E0EBFF',
                color: isRefreshing ? '#999' : '#0063F7',
                boxShadow: isRefreshing ? 0 : 6
              }
            }}
          >
            {isRefreshing ? 'Updating...' : 'Edit'}
          </Button>
          <Button
            variant="contained"
            disabled={isRefreshing}
            sx={{
              width: { xs: '100%', sm: 160 },
              height: 60,
              borderRadius: '8px',
              p: '16px 12px 16px 8px',
              gap: '4px',
              boxShadow: 0,
              backgroundColor: isRefreshing ? '#ccc' : '#0063F7 !important',
              color: '#FFFFFF !important',
              fontFamily: 'Roboto',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '24px',
              letterSpacing: '0.01em',
              '&:hover': {
                backgroundColor: isRefreshing ? '#ccc' : '#0063F7 !important',
                color: '#FFFFFF !important',
                boxShadow: 10
              }
            }}
            onClick={() => {
              setRenewMode("update");
              setShowUploadModal(true);
            }}
          >
            {isRefreshing ? 'Updating...' : 'Renew'}
          </Button>
        </Box>
      )}

      {/* Upload and Update Flow */}
      <UploadPolicyModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onExtracted={(data) => {
          setPrefillData(data);
          setShowUploadModal(false);
          setShowRenewForm(true);
        }}
        user={user}
      />
      {showRenewForm && (
        <RenewPolicy
          isOpen={showRenewForm}
          onClose={() => {
            setShowRenewForm(false);
            setPrefillData(null);
          }}
          prefillData={prefillData}
          mode={renewMode}
          existingPolicyId={policyId}
          existingCustomerId={policy?.customerId}
          agentId={policy?.agentId}
          initialCustomerData={{
            name: policy?.name || "",
            mobile: policy?.mobile || "",
            email: policy?.email || "",
            address: policy?.address || ""
          }}
          onSuccess={handlePolicyUpdateSuccess}
        />
      )}

      {/* Success Dialog */}
      <SuccessDialog
        isOpen={showSuccessDialog}
        pdfUrl={successPdfUrl}
        headerText="Renewal Success"
        messageText="Policy renewal completed. Share with customer via WhatsApp."
        agentName={user?.displayName || "Your Agent"}
        premiumAmount={policy?.amountPayable || (parseFloat(policy?.premiumAmount) - (parseFloat(policy?.discount) || 0))}
        balanceAmount={Math.max((Number(policy?.amountPayable || (parseFloat(policy?.premiumAmount) - (parseFloat(policy?.discount) || 0))) || 0) - (Number(policy?.paidAmount) || 0), 0)}
        onClose={() => setShowSuccessDialog(false)}
      />

    </Box>
  );
};

export default PolicyDetail;